import * as React from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, Search, Download, RotateCcw } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

import { Button } from "./button"
import { Input } from "./input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Skeleton } from "./skeleton"
import { cn } from "../../lib/utils"

interface DataTableProps {
  columns: ColumnDef<any>[]
  data: any[]
  title?: string
  loading?: boolean
  onRefresh?: () => void
  searchPlaceholder?: string
  searchableColumns?: string[]
  enableExport?: boolean
  exportFileName?: string
  className?: string
  pageSize?: number
}

function TableSkeleton({ columns }: { columns: ColumnDef<any>[] }) {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          {columns.map((_, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton className="h-5 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function DataTable({
  columns,
  data,
  title,
  loading = false,
  onRefresh,
  searchPlaceholder = "Buscar...",
  searchableColumns = [],
  enableExport = true,
  exportFileName = "data",
  className,
  pageSize = 10,
}: DataTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [globalFilter, setGlobalFilter] = React.useState("")

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    initialState: {
      pagination: {
        pageSize: pageSize,
      },
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
  })

  const exportToCSV = React.useCallback(() => {
    const headers = table.getAllColumns()
      .filter((column) => column.getIsVisible() && column.id !== "actions")
      .map((column) => column.id)

    const csvContent = [
      headers.join(","),
      ...table.getFilteredRowModel().rows.map((row) =>
        headers.map((header) => {
          const value = row.getValue(header)
          return typeof value === "string" ? `"${value}"` : value
        }).join(",")
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${exportFileName}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }, [table, exportFileName])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={className}>
        {title && (
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="flex items-center gap-4 py-4">
            <motion.div
              className="relative flex-1 max-w-sm"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter}
                onChange={(event) => setGlobalFilter(event.target.value)}
                className="pl-8 transition-all focus:ring-2 focus:ring-primary/20"
              />
            </motion.div>

            <motion.div
              className="flex items-center gap-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {onRefresh && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={loading}
                  className="hover:bg-primary/10 transition-colors"
                >
                  <RotateCcw className={cn("h-4 w-4", loading && "animate-spin")} />
                  Atualizar
                </Button>
              )}

              {enableExport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  disabled={loading}
                  className="hover:bg-primary/10 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Exportar
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="hover:bg-primary/10 transition-colors">
                    Colunas <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[150px]">
                  {table
                    .getAllColumns()
                    .filter(
                      (column) =>
                        typeof column.accessorFn !== "undefined" && column.getCanHide()
                    )
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {column.id}
                        </DropdownMenuCheckboxItem>
                      )
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </motion.div>
          </div>

          <motion.div
            className="rounded-md border"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      const isStringHeader = typeof header.column.columnDef.header === "string";
                      return (
                        <TableHead
                          key={header.id}
                          className={cn(canSort && "select-none")}
                          onClick={
                            canSort
                              ? (e) => {
                                  if ((e.target as Element).closest("button")) return;
                                  header.column.toggleSorting(sorted === "asc");
                                }
                              : undefined
                          }
                        >
                          {header.isPlaceholder ? null : isStringHeader ? (
                            <button
                              className={cn(
                                "flex items-center gap-1 text-left font-medium",
                                canSort && "cursor-pointer hover:text-foreground"
                              )}
                              onClick={canSort ? () => header.column.toggleSorting(sorted === "asc") : undefined}
                              disabled={!canSort}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {canSort && (
                                sorted === "asc" ? (
                                  <ArrowUp className="h-4 w-4 text-primary" />
                                ) : sorted === "desc" ? (
                                  <ArrowDown className="h-4 w-4 text-primary" />
                                ) : (
                                  <ArrowUpDown className="h-4 w-4 opacity-40" />
                                )
                              )}
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                <AnimatePresence mode="wait">
                  {loading ? (
                    <TableSkeleton columns={columns} />
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row, index) => (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03, duration: 0.2 }}
                        data-state={row.getIsSelected() && "selected"}
                        className={cn(
                          "hover:bg-muted/50 transition-colors border-b",
                          index % 2 === 1 && "bg-muted/20"
                        )}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </motion.tr>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          Nenhum resultado.
                        </motion.div>
                      </TableCell>
                    </TableRow>
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </motion.div>

          <motion.div
            className="flex items-center justify-between space-x-2 py-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length > 0 && (
                <span>
                  {table.getFilteredSelectedRowModel().rows.length} de{" "}
                  {table.getFilteredRowModel().rows.length} linha(s) selecionada(s)
                </span>
              )}
              {table.getFilteredSelectedRowModel().rows.length === 0 && (
                <span>
                  {table.getFilteredRowModel().rows.length} linha(s) total
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage() || loading}
                className="hover:bg-primary/10 transition-colors"
              >
                Anterior
              </Button>
              <div className="flex items-center justify-center text-sm font-medium">
                Página {table.getState().pagination.pageIndex + 1} de{" "}
                {table.getPageCount()}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage() || loading}
                className="hover:bg-primary/10 transition-colors"
              >
                Próxima
              </Button>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export function createSortableHeader(title: string) {
  return ({ column }: any) => {
    const sorted = column.getIsSorted();
    return (
      <Button
        variant="ghost"
        className="h-auto p-0 hover:bg-transparent"
        onClick={() => column.toggleSorting(sorted === "asc")}
      >
        {title}
        {sorted === "asc" ? (
          <ArrowUp className="ml-2 h-4 w-4 text-primary" />
        ) : sorted === "desc" ? (
          <ArrowDown className="ml-2 h-4 w-4 text-primary" />
        ) : (
          <ArrowUpDown className="ml-2 h-4 w-4 opacity-40" />
        )}
      </Button>
    )
  }
}
